import { Steps } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { Col, Row, Table, Card, Dropdown, Modal } from "react-bootstrap";
import ReactQuill from "react-quill-new";
import { FiSearch, FiCheck, FiInfo } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "../../../components/Breadcrumbs";
import Button from "../../../components/button";
import Input from "../../../components/input";
import Select from "../../../components/Select";
import { apiService } from "../../../services/apiService";
import "../../../styles/Access.css";
import "../../../styles/AssetsRegistration.css";
import "../../../styles/DriverPayments.css";
import "../../../styles/DriverSafety.css";
import "../../../styles/Input.css";
import "../../../styles/Invoices.css";
import "../../../styles/Issue.css";
import "../../../styles/Trip.css";
import "../../../styles/TripCostEstimation.css";
import "../../../styles/TransporterSteps.css";
import { fetchCustomerDropdown } from "../../../store/Slices/dropdownSlice";
import {
  AppMainModules,
  FinancePermission,
  FinanceSubModules,
  INVOICE_TYPES,
} from "../../../utils/app-constant";
import { formats, getCurrentUser, hasPermission, modules } from "../../../utils/helper";
import { errorNotify, successNotify } from "../../../utils/toast";
import { formatProvinceLabel } from "../../../utils/provinceOptions";

import {
  buildBulkInvoiceRecordFromForm,
  formatAmount,
  getInvoiceRecords,
  getNextInvoicePreviewId,
  saveInvoiceRecords,
} from "./Invoices";
import SpinnerLoader from "../../../utils/SpinnerLoader";

const getFirstDefinedValue = (candidates = [], fallback = "") => {
  for (const candidate of candidates) {
    if (candidate === "" || candidate === null || candidate === undefined) {
      continue;
    }
    return candidate;
  }
  return fallback;
};

const toTrimmedText = (value = "") => String(value ?? "").trim();

const stripTrailingIdentifier = (value = "") =>
  toTrimmedText(value).replace(/\s*\([^)]*\)\s*$/u, "").trim();

const toApiIdentifier = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const parsedValue = Number(value);
  if (Number.isFinite(parsedValue)) {
    return parsedValue;
  }
  return toTrimmedText(value);
};

const toApiIdentifierList = (values = []) =>
  (Array.isArray(values) ? values : [])
    .map((value) => toApiIdentifier(value))
    .filter((value) => value !== null && value !== "");

const formatLineFormula = (values = []) => values.filter(Boolean).join(" | ");

const formatExpenseLabel = (key = "") => {
  const labelLookup = {
    total_fuel_expense: "Fuel Expense",
    total_shortage_expense: "Shortage Expense",
    total_insurance_expense: "Insurance Expense",
    total_operational_expenses: "Operational Expense",
  };
  return labelLookup[key] || key;
};

const formatSettlementType = (type = "") =>
  String(type || "")
    .split("_")
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");

const prependMissingOption = (options = [], nextOption = null) => {
  if (!nextOption?.value) return Array.isArray(options) ? options : [];
  const safeOptions = Array.isArray(options) ? options : [];
  const alreadyExists = safeOptions.some(
    (option) => String(option?.value ?? "") === String(nextOption?.value ?? "")
  );
  if (alreadyExists) return safeOptions;
  return [nextOption, ...safeOptions];
};

const mapCustomerContractsToOptions = (contracts = []) =>
  (Array.isArray(contracts) ? contracts : [])
    .map((contract) => {
      const contractId = toTrimmedText(
        getFirstDefinedValue([contract?.contract_id, contract?.contracts_id, contract?.id, contract?.value], "")
      );
      const contractName = stripTrailingIdentifier(
        getFirstDefinedValue([
          contract?.name,
          contract?.label,
          contract?.contracts_template_id,
          contract?.contract_name,
          contract?.title,
          contract?.contract?.name,
          contract?.contract?.label,
          contract?.contract?.contracts_template_id,
          contract?.contract?.contract_name,
          contract?.contract_template?.name,
          contract?.contract_template?.contracts_template_id,
        ], "")
      );
      if (!contractId && !contractName) {
        return null;
      }
      return {
        value: contractId,
        label: contractName || `Contract ${contractId}`,
        payment_term: contract?.payment_term || contract?.payment_terms || contract?.paymentTerms || "",
      };
    })
    .filter(Boolean);

const getContractDisplayNameFromRecord = (item = {}, fallback = "") => {
  const contract = item?.contract || item?.contracts || item?.contract_template || {};
  const contractName = getFirstDefinedValue([
    contract?.name,
    contract?.label,
    contract?.contracts_template_id,
    contract?.contract_name,
    contract?.title,
    item?.contract_name,
    item?.contract_label,
    item?.contracts_template_id,
  ], "");

  return stripTrailingIdentifier(contractName) || fallback;
};

const mapDraftTripsToOptions = (trips = []) =>
  (Array.isArray(trips) ? trips : [])
    .map((trip) => {
      const tripRecordId = toTrimmedText(
        getFirstDefinedValue([trip?.id, trip?.value, trip?.trip_record_id], "")
      );
      const tripCode = toTrimmedText(
        getFirstDefinedValue([trip?.trip_id, trip?.label, trip?.name], "")
      );
      if (!tripRecordId && !tripCode) {
        return null;
      }
      return {
        ...trip,
        value: tripRecordId || tripCode,
        label: tripCode || `Trip ${tripRecordId}`,
        tripRecordId: tripRecordId || tripCode,
        tripCode: tripCode || tripRecordId,
      };
    })
    .filter(Boolean);

const toDateOnlyStr = (date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const getDefaultInvoiceAt = () => toDateOnlyStr(new Date());

const getDefaultInvoiceDueAt = () => {
  const dueDate = new Date();
  dueDate.setMonth(dueDate.getMonth() + 1);
  return toDateOnlyStr(dueDate);
};

const formatDateTimeForPayload = (dtStr, type = "start") => {
  if (!dtStr) return "";
  let replaced = String(dtStr).trim().replace("T", " ");
  if (replaced.length === 10) {
    return type === "start" ? `${replaced} 00:00:00` : `${replaced} 23:59:59`;
  }
  if (replaced.split(":").length === 2) {
    return `${replaced}:00`;
  }
  return replaced;
};

// Percentage inputs (Rental Tax / Trip Tax) must stay within 0-100.
const clampPercentageInput = (rawValue) => {
  if (rawValue === "" || rawValue === null || rawValue === undefined) return "";
  const parsed = Number(rawValue);
  if (!Number.isFinite(parsed)) return rawValue;
  if (parsed > 100) return "100";
  if (parsed < 0) return "0";
  return rawValue;
};

const toPercentageNumber = (rawValue) => {
  const parsed = Number(rawValue);
  if (!Number.isFinite(parsed)) return 0;
  return Math.min(100, Math.max(0, parsed));
};

const buildCalculationPreviewPayload = (values = {}) => {
  const customerId = toApiIdentifier(values?.customerId);
  const contractId = toApiIdentifier(values?.contractId);
  const tripIds = toApiIdentifierList(values?.selectedTripIds);
  const rentalTaxPercentage = toPercentageNumber(values?.rental_tax_percentage);
  const tripTaxPercentage = toPercentageNumber(values?.trip_tax_percentage);

  return {
    contract_id: contractId,
    customer_id: customerId,
    from: formatDateTimeForPayload(values?.startAt, "start"),
    to: formatDateTimeForPayload(values?.endAt, "end"),
    start_at: formatDateTimeForPayload(values?.startAt, "start"),
    end_at: formatDateTimeForPayload(values?.endAt, "end"),
    invoice_type: values?.invoiceType || INVOICE_TYPES.MONTHLY,
    trip_ids: tripIds,
    include_monthly_rental: Boolean(values?.includeMonthlyRental),
    rental_tax_percentage: rentalTaxPercentage,
    trip_tax_percentage: tripTaxPercentage,
  };
};

const buildBulkTripInvoiceCreatePayload = (values = {}) => {
  const customerId = toApiIdentifier(values?.customerId);
  const contractId = toApiIdentifier(values?.contractId);
  const tripIds = toApiIdentifierList(values?.selectedTripIds);
  const rentalTaxPercentage = toPercentageNumber(values?.rental_tax_percentage);
  const tripTaxPercentage = toPercentageNumber(values?.trip_tax_percentage);
  return {
    contract_id: contractId,
    customer_id: customerId,
    trips_count: tripIds.length,
    trip_ids: tripIds,
    start_at: formatDateTimeForPayload(values?.startAt, "start"),
    end_at: formatDateTimeForPayload(values?.endAt, "end"),
    invoice_at: values?.invoice_at || null,
    invoice_due_at: values?.invoice_due_at || null,
    invoice_type: values?.invoiceType,
    notes: toTrimmedText(values?.notes),
    include_monthly_rental: Boolean(values?.includeMonthlyRental),
    rental_tax_percentage: rentalTaxPercentage,
    trip_tax_percentage: tripTaxPercentage,
  };
};

const parseFiniteNumber = (value, fallback = 0) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

const pickInvoiceNumber = (values = [], fallback = 0) => {
  const parsedValues = values
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value));
  const nonZeroValue = parsedValues.find((value) => value !== 0);
  return nonZeroValue ?? parsedValues[0] ?? fallback;
};

const calculateAverageFuelPrice = (fuelLogs = []) => {
  if (!Array.isArray(fuelLogs) || !fuelLogs.length) return 0;

  const validPrices = fuelLogs
    .map((log) => Number(log?.fuel_price || 0))
    .filter((price) => Number.isFinite(price));

  if (!validPrices.length) return 0;

  const total = validPrices.reduce((sum, price) => sum + price, 0);
  return Number((total / validPrices.length).toFixed(2));
};

const buildTaxRuleDetails = (item = {}, legs = []) => {
  const normalizedLegs = Array.isArray(legs) ? legs : [];

  if (!normalizedLegs.length) {
    const record = item?.trips_invoice_record || {};
    const taxAmount = pickInvoiceNumber([
      item?.invoice_tax_amount,
      item?.tax_amount,
      record?.tax_amount,
    ]);
    const taxPercentage = pickInvoiceNumber([
      item?.invoice_tax_rate,
      item?.tax_percent,
      record?.tax_rate,
    ]);

    return taxAmount || taxPercentage
      ? [
          {
            id: record?.tax_rule_id || item?.tax_rule_id || "",
            name: "Tax Rule",
            route: "",
            taxNumber: item?.tax_registration_number || "",
            taxPercentage,
            freightAmount: pickInvoiceNumber([
              item?.invoice_taxable_amount,
              record?.freight,
              item?.invoice_gross_amount,
            ]),
            taxAmount,
          },
        ]
      : [];
  }

  return normalizedLegs.map((leg, index) => {
    const taxRule = leg?.tax_rule || {};
    const sourceProvince = formatProvinceLabel(
      leg?.source_stop?.province || taxRule?.source_province || leg?.source_province || ""
    );
    const destinationProvince = formatProvinceLabel(
      leg?.destination_stop?.province ||
      taxRule?.destination_province ||
      leg?.destination_province ||
      ""
    );

    return {
      id: leg?.tax_rule_id || taxRule?.id || "",
      name: taxRule?.name || "Tax Rule",
      route:
        sourceProvince || destinationProvince
          ? `${sourceProvince || "-"} to ${destinationProvince || "-"}`
          : "",
      taxNumber: taxRule?.tax_number || item?.tax_registration_number || "",
      taxPercentage: pickInvoiceNumber([
        leg?.tax_percentage,
        taxRule?.tax_percentage,
        item?.invoice_tax_rate,
        item?.tax_percent,
      ]),
      freightAmount: pickInvoiceNumber([
        item?.invoice_taxable_amount,
        leg?.freight_amount,
        item?.invoice_gross_amount,
      ]),
      taxAmount: pickInvoiceNumber([
        leg?.tax_amount,
        item?.invoice_tax_amount,
        item?.tax_amount,
      ]),
      key: leg?.id || `${leg?.tax_rule_id || taxRule?.id || "tax"}-${index}`,
    };
  });
};

const normalizeTripData = (item, contractDisplayName = "") => {
  if (!item) return null;
  const trip = item.trip || {};
  const record = item.trips_invoice_record || {};
  const legs = item.trips_legs || trip.trips_legs || [];

  const tripId = trip.id || item.trip_id || item.id;
  let tripCode = trip.trip_id || "";
  if (!tripCode) {
    const directVal = item.trip_id || item.id;
    tripCode = directVal ? (String(directVal).startsWith("TR-") ? String(directVal) : `TR-${directVal}`) : `TR-${tripId}`;
  }

  const freight = Number(record.freight || item.invoice_gross_amount || 0);
  const deduction = Number(record.deduction || item.total_shortage_expense || 0);
  const net = Number(item.invoice_net_amount || record.net_line || 0);

  const weight = Number(
    trip.trip_load_sum?.total_load ||
    record.weight ||
    item.weight ||
    item.cargo_weight ||
    trip.weight ||
    trip.cargo_weight ||
    item.trip_data?.load_quantity ||
    item.trip_data?.weight ||
    0
  );
  const cargo_weight = Number(
    trip.trip_load_sum?.total_load ||
    trip.cargo_weight ||
    item.cargo_weight ||
    trip.weight ||
    item.weight ||
    item.trip_data?.load_quantity ||
    item.trip_data?.weight ||
    record.weight ||
    0
  );
  const fuelQty = Number(trip.utilized_fuel || 0);
  const averageFuelPrice = calculateAverageFuelPrice(trip.fuel_logs);
  const distance = Number(trip.distance || 0);
  const shortage = Number(trip.shortage_qty || 0);
  const legTaxAmount = legs.reduce(
    (sum, leg) => sum + parseFiniteNumber(leg?.tax_amount, 0),
    0
  );

  const tax = pickInvoiceNumber([
    item.invoice_tax_amount,
    item.tax_amount,
    legTaxAmount,
    record.tax_amount,
  ]);

  const taxRate = legs.length > 0
    ? Number(legs[0].tax_percentage || legs[0].tax_rule?.tax_percentage || item.invoice_tax_rate || item.tax_percent || record.tax_rate || 0)
    : Number(record.tax_rate || item.tax_percent || item.invoice_tax_rate || 0);

  const settlements = item.settlement_details || [];
  const freightSettlement = settlements.find(s => s.type === "formula");
  const shortageSettlement = settlements.find(s => s.type === "shortage");

  const freightFormulaText = freightSettlement
    ? `${freightSettlement.expression || "Freight Base"} (${freightSettlement.formula}) = PKR ${Number(freightSettlement.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`
    : "—";
  const shortageFormulaText = shortageSettlement
    ? `${shortageSettlement.expression || "Shortage"} (${shortageSettlement.formula}) = PKR ${Number(shortageSettlement.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`
    : "—";

  const firstSrc = legs[0]?.source_stop?.province || legs[0]?.province || "—";
  const lastDest = legs[legs.length - 1]?.destination_stop?.province || "—";
  const startLocation = trip?.start_trip?.location;
  const endLocation = trip?.end_trip?.location;
  const routeLabel = legs.length > 0
    ? `${firstSrc} → ${lastDest}`
    : (startLocation && endLocation
      ? `${startLocation} → ${endLocation}`
      : (trip.route || "—"));

  return {
    id: String(tripId),
    value: String(tripId),
    label: tripCode,
    tripCode,
    formula: record.formula_text || item.formula || "—",
    vehicleTypeName: (item.vehicle_type?.title || item.vehicle_type?.name || trip.vehicle_type?.name || "—")
      .replace(/[-_]/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase()),
    vehicleNo: item.vehicle?.registration_number || item.vehicle?.registration_no || trip.vehicle?.vehicle_no || trip.vehicle?.registration_number || trip.vehicle?.registration_no || "—",
    legs,
    weight,
    cargo_weight,
    fuelQty,
    averageFuelPrice,
    shortageQty: shortage,
    freight,
    deduction,
    taxRate,
    tax,
    net,
    workingDetails: {
      contractVersion: getContractDisplayNameFromRecord(item, contractDisplayName || "Contract"),
      vehicleType: (item.vehicle_type?.title || item.vehicle_type?.name || "—")
        .replace(/[-_]/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase()),
      route: routeLabel,
      legs,
      weight,
      fuelQty,
      distance,
      shortage,
      odometerDistance: Number(item.trip_data?.odometer_distance || 0),
      loadQuantity: Number(item.trip_data?.load_quantity || 0),
      formulaText: record.formula_text || "—",
      shortageText: record.working_logs || `shortage: ${shortage} → deduction PKR ${deduction.toLocaleString()}`,
      taxRuleMatched: legs[0]?.tax_rule_id ? `Tax Rule Match ID: ${legs[0].tax_rule_id}` : "—",
      taxRegistrationNumber: item.tax_registration_number || "—",
      taxRules: buildTaxRuleDetails(item, legs),
      taxAmount: tax,
      finalAmount: net,
      freightFormulaText,
      shortageFormulaText,
      workingLogs: record.working_logs || "",
    },
    raw: item
  };
};

const buildTripInvoiceInfoCalculation = (responseData = {}) => {
  const invoiceInfoData = responseData?.data || responseData || {};
  const trips = Array.isArray(invoiceInfoData?.trips) ? invoiceInfoData.trips : [];

  let calculatedSubtotal = 0;
  let calculatedDiscount = 0;
  let calculatedTax = 0;
  let calculatedTotal = 0;

  const lineItems = [];
  trips.forEach((tripItem, tripIndex) => {
    const norm = normalizeTripData(tripItem);
    if (!norm) return;

    calculatedSubtotal += norm.freight;
    calculatedDiscount += norm.deduction;
    calculatedTax += norm.tax;
    calculatedTotal += norm.net;

    const settlementDetails = Array.isArray(tripItem?.settlement_details)
      ? tripItem.settlement_details
      : [];
    settlementDetails.forEach((settlementDetail, detailIndex) => {
      lineItems.push({
        id:
          settlementDetail?.id ||
          `settlement-${tripIndex}-${detailIndex}-${settlementDetail?.type || "line"}`,
        title: toTrimmedText(settlementDetail?.name) || "Settlement Detail",
        formula: formatLineFormula([
          norm.tripCode,
          formatSettlementType(settlementDetail?.type),
        ]),
        amount: toNumber(settlementDetail?.amount),
      });
    });
    [
      "total_fuel_expense",
      "total_shortage_expense",
      "total_insurance_expense",
      "total_operational_expenses",
    ].forEach((expenseKey) => {
      const expenseAmount = toNumber(tripItem?.[expenseKey]);
      if (!expenseAmount) {
        return;
      }
      lineItems.push({
        id: `${expenseKey}-${tripItem?.id || tripIndex}`,
        title: formatExpenseLabel(expenseKey),
        formula: formatLineFormula([norm.tripCode, "Expense Deduction"]),
        amount: -expenseAmount,
      });
    });
  });

  return {
    lineItems,
    subtotal: invoiceInfoData?.subtotal !== undefined ? toNumber(invoiceInfoData.subtotal) : calculatedSubtotal,
    salesTax: invoiceInfoData?.tax !== undefined ? toNumber(invoiceInfoData.tax) : calculatedTax,
    discount: invoiceInfoData?.discount !== undefined ? toNumber(invoiceInfoData.discount) : calculatedDiscount,
    totalAmount: invoiceInfoData?.total_amount !== undefined ? toNumber(invoiceInfoData.total_amount) : calculatedTotal,
    rawData: invoiceInfoData,
  };
};

const INVOICE_STEPS = [
  { title: "Customer & Period" },
  { title: "Eligible Trips" },
  { title: "Calculation Preview" },
  { title: "Invoice Preview" },
];

const toNumber = (value, fallback = 0) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

const renderFieldLabel = (label, required = true) => (
  <span className="assets_vehicle_field_label invoice_field_label">
    <span>{label}</span>
    {required ? <span className="assets_vehicle_field_required">*</span> : null}
  </span>
);

const pickOption = (options = [], value = "") =>
  (Array.isArray(options) ? options : []).find(
    (option) => String(option?.value ?? "") === String(value ?? "")
  ) || null;

const isEmptyValue = (value) => String(value ?? "").trim() === "";
const safeArray = (value) => (Array.isArray(value) ? value : []);

// Drawer overlay for Calculation working details
const CalculationWorkingDrawer = ({ show, onClose, title, data }) => {
  if (!show) return null;
  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0, 0, 0, 0.4)",
          zIndex: 1050,
        }}
      />
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          width: "480px",
          height: "100%",
          background: "#fff",
          boxShadow: "-4px 0 16px rgba(0,0,0,0.12)",
          zIndex: 1060,
          padding: "24px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
            borderBottom: "1px solid #e2e8f0",
            paddingBottom: "12px",
          }}
        >
          <h5 style={{ margin: 0, fontSize: "18px", fontWeight: 600, color: "#1e293b" }}>
            {title}
          </h5>
          <button
            onClick={onClose}
            style={{
              border: "none",
              background: "none",
              fontSize: "24px",
              cursor: "pointer",
              color: "#64748b",
              lineHeight: 1,
            }}
          >
            &times;
          </button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", paddingRight: "4px" }}>
          {data ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <div>
                <span style={{ fontSize: "12px", color: "#64748b", display: "block" }}>Contract version used:</span>
                <strong style={{ fontSize: "14px", color: "#334155" }}>{data.contractVersion || "Contract"}</strong>
              </div>
              <div>
                <span style={{ fontSize: "12px", color: "#64748b", display: "block" }}>Vehicle formula:</span>
                <strong style={{ fontSize: "14px", color: "#334155" }}>{data.vehicleType || "Container"}</strong>
              </div>
              <div>
                <span style={{ fontSize: "12px", color: "#64748b", display: "block", marginBottom: "4px" }}>Route:</span>
                {data.legs && data.legs.length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: "6px", background: "#f8fafc", padding: "10px 14px", borderRadius: "6px", border: "1px solid #e2e8f0" }}>
                    {data.legs.map((leg, index) => {
                      const s = leg?.source_stop?.province || leg?.province || "—";
                      const d = leg?.destination_stop?.province || "—";
                      return (
                        <div key={index} style={{ fontSize: "13px", color: "#334155", fontWeight: 500 }}>
                          {s} &rarr; {d}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <strong style={{ fontSize: "14px", color: "#334155" }}>{data.route || "—"}</strong>
                )}
              </div>
              <div style={{ background: "#f8fafc", padding: "14px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                <span style={{ fontSize: "12px", color: "#475569", display: "block", marginBottom: "10px", fontWeight: 600 }}>Input Values:</span>
                <div style={{ display: "flex", flexDirection: "column", gap: "6px", fontSize: "13px", color: "#334155" }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>Weight</span>
                    <strong>{data.weight}</strong>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>Fuel Litres</span>
                    <strong>{data.fuelQty}</strong>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>Distance (Km)</span>
                    <strong>{data.distance}</strong>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>Shortage</span>
                    <strong>{data.shortage}</strong>
                  </div>
                  {data.odometerDistance !== undefined && data.odometerDistance > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span>Odometer Distance (Km)</span>
                      <strong>{data.odometerDistance}</strong>
                    </div>
                  )}
                  {data.loadQuantity !== undefined && data.loadQuantity > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span>Load Quantity</span>
                      <strong>{data.loadQuantity}</strong>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <span style={{ fontSize: "12px", color: "#64748b", display: "block" }}>Freight Formula:</span>
                <strong style={{ fontSize: "14px", color: "#0f766e" }}>{data.freightFormulaText || data.formulaText || "—"}</strong>
              </div>
              <div>
                <span style={{ fontSize: "12px", color: "#64748b", display: "block" }}>Shortage Formula:</span>
                <strong style={{ fontSize: "14px", color: "#dc2626" }}>{data.shortageFormulaText || data.shortageText || "—"}</strong>
              </div>
              {data.workingLogs && (
                <div>
                  <span style={{ fontSize: "12px", color: "#64748b", display: "block" }}>Calculation Logs:</span>
                  <pre style={{
                    fontSize: "12px",
                    color: "#334155",
                    background: "#f1f5f9",
                    padding: "10px",
                    borderRadius: "6px",
                    fontFamily: "monospace",
                    whiteSpace: "pre-wrap",
                    margin: "4px 0 0 0",
                    border: "1px solid #e2e8f0"
                  }}>
                    {data.workingLogs}
                  </pre>
                </div>
              )}
              {Array.isArray(data.taxRules) && data.taxRules.length > 0 && (
                <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "14px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                    <div>
                      <span style={{ fontSize: "12px", color: "#64748b", display: "block", fontWeight: 600 }}>Tax Breakdown</span>
                      <strong style={{ fontSize: "14px", color: "#1e293b" }}>
                        {data.taxRules.length} matched rule{data.taxRules.length > 1 ? "s" : ""}
                      </strong>
                    </div>
                    <strong style={{ fontSize: "14px", color: "#0f766e" }}>
                      PKR {formatAmount(data.taxAmount || 0)}
                    </strong>
                  </div>
                  <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: "8px", background: "#ffffff" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "560px" }}>
                      <thead>
                        <tr style={{ background: "#f1f5f9" }}>
                          {["Rule", "Route", "TRN", "%", "Taxable", "Tax"].map((heading) => (
                            <th
                              key={heading}
                              style={{
                                padding: "8px 10px",
                                fontSize: "11px",
                                color: "#475569",
                                fontWeight: 700,
                                textAlign: heading === "%" || heading === "Taxable" || heading === "Tax" ? "right" : "left",
                                borderBottom: "1px solid #e5e7eb",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {heading}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {data.taxRules.map((rule, index) => (
                          <tr key={rule.key || `${rule.id || "tax"}-${index}`}>
                            <td style={{ padding: "9px 10px", fontSize: "12px", color: "#334155", fontWeight: 600, borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>
                              {rule.name || "Tax Rule"}{rule.id ? ` #${rule.id}` : ""}
                            </td>
                            <td style={{ padding: "9px 10px", fontSize: "12px", color: "#334155", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>
                              {rule.route || "-"}
                            </td>
                            <td style={{ padding: "9px 10px", fontSize: "12px", color: "#334155", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>
                              {rule.taxNumber || "-"}
                            </td>
                            <td style={{ padding: "9px 10px", fontSize: "12px", color: "#0f766e", fontWeight: 700, textAlign: "right", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>
                              {Number(rule.taxPercentage || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%
                            </td>
                            <td style={{ padding: "9px 10px", fontSize: "12px", color: "#334155", textAlign: "right", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>
                              {formatAmount(rule.freightAmount || 0)}
                            </td>
                            <td style={{ padding: "9px 10px", fontSize: "12px", color: "#334155", fontWeight: 700, textAlign: "right", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>
                              {formatAmount(rule.taxAmount || 0)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {!(Array.isArray(data.taxRules) && data.taxRules.length > 0) && (
                <>
              <div>
                <span style={{ fontSize: "12px", color: "#64748b", display: "block" }}>Percentage:</span>
                <span style={{ fontSize: "14px", color: "#334155" }}>
                  {Number.isFinite(Number(data.taxPercentage)) ? `${Number(data.taxPercentage).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%` : "—"}
                </span>
              </div>
              <div>
                <span style={{ fontSize: "12px", color: "#64748b", display: "block" }}>Tax amount:</span>
                <strong style={{ fontSize: "14px", color: "#334155" }}>PKR {formatAmount(data.taxAmount || 0)}</strong>
              </div>
                </>
              )}
              <div style={{ borderTop: "1px dashed #cbd5e1", paddingTop: "12px", marginTop: "8px" }}>
                <span style={{ fontSize: "13px", color: "#64748b" }}>Final amount:</span>
                <h4 style={{ margin: 0, color: "#1e3a8a", fontWeight: 700 }}>PKR {formatAmount(data.finalAmount || 0)}</h4>
              </div>
            </div>
          ) : (
            <p style={{ color: "#64748b" }}>No working details available for this trip.</p>
          )}
        </div>
      </div>
    </>
  );
};

const CreateInvoice = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const currentUser = getCurrentUser();
  const customerRequestIdRef = useRef(0);
  const tripRequestIdRef = useRef(0);
  const invoiceInfoRequestIdRef = useRef(0);

  const canCreateInvoice = hasPermission({
    permissionSlug: FinancePermission.Create,
    menuSlug: FinanceSubModules.Invoices,
    moduleSlug: AppMainModules.finance,
  });

  const { customerDropdownData, loading: dropdownLoading } = useSelector(
    (state) => state.dropdowns
  );

  const [pageLoading, setPageLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [currentFormStep, setCurrentFormStep] = useState(0);

  // Form state fields
  const [invoiceValues, setInvoiceValues] = useState(() => {
    return {
      invoiceId: "INV-1001",
      customerId: "",
      customerLabel: "",
      contractId: "",
      contractLabel: "",
      billingCadence: "—",
      invoiceType: "",
      periodFrom: "",
      periodTo: "",
      startAt: "",
      endAt: "",
      invoice_at: getDefaultInvoiceAt(),
      invoice_due_at: getDefaultInvoiceDueAt(),
      includeMonthlyRental: false,
      rental_tax_percentage: "",
      trip_tax_percentage: "",
      deliveryOrder: "",
      selectedTripIds: [],
      selectedTrips: [],
      invoiceInfoCalculation: null,
      invoiceInfoRawData: null,
      notes: "",
      status: "new",
    };
  });

  const [invoiceErrors, setInvoiceErrors] = useState({});
  const [invoiceSaving, setInvoiceSaving] = useState(false);
  const [contractOptions, setContractOptions] = useState([]);
  const [tripOptions, setTripOptions] = useState([]);
  const [invoiceColumns, setInvoiceColumns] = useState([]);
  const [contractLoading, setContractLoading] = useState(false);
  const [tripLoading, setTripLoading] = useState(false);
  const [invoiceInfoLoading, setInvoiceInfoLoading] = useState(false);
  const [columnConfig, setColumnConfig] = useState(null);
  const [selectedPaymentTerm, setSelectedPaymentTerm] = useState("");

  // Search filter for Step 2 trips checklist
  const [tripSearch, setTripSearch] = useState("");

  // Calculation Working Drawer state
  const [workingDrawerOpen, setWorkingDrawerOpen] = useState(false);
  const [workingDrawerTitle, setWorkingDrawerTitle] = useState("");
  const [workingDrawerData, setWorkingDrawerData] = useState(null);

  // Legs Modal state
  const [legsModalOpen, setLegsModalOpen] = useState(false);
  const [legsModalTitle, setLegsModalTitle] = useState("");
  const [legsModalData, setLegsModalData] = useState([]);

  // Initialize
  useEffect(() => {
    if (!Array.isArray(customerDropdownData) || !customerDropdownData.length) {
      dispatch(fetchCustomerDropdown());
    }
  }, [customerDropdownData, dispatch]);

  useEffect(() => {
    const loadNextInvoiceId = async () => {
      try {
        const response = await apiService.getTripsInvoicesPaginated({ page: 1, limit: 100 });
        const invoicePageData = response?.data || response || {};
        const results = Array.isArray(invoicePageData?.results)
          ? invoicePageData.results
          : Array.isArray(invoicePageData)
            ? invoicePageData
            : [];

        const maxSequence = Math.max(
          0,
          ...results.map((record) => {
            const code = record?.trips_invoice_id || record?.invoiceId || "";
            const matchedDigits = String(code).match(/(\d+)/g);
            if (!matchedDigits?.length) return 0;
            return Number(matchedDigits[matchedDigits.length - 1]) || 0;
          })
        );

        setInvoiceValues((prev) => ({
          ...prev,
          invoiceId: `TINV-${maxSequence + 1}`,
        }));
      } catch (error) {
        setInvoiceValues((prev) => ({
          ...prev,
          invoiceId: "TINV-1",
        }));
      } finally {
        setPageLoading(false);
      }
    };

    loadNextInvoiceId();
  }, []);

  // Fetch contracts when Customer changes
  useEffect(() => {
    const nextCustomerId = String(invoiceValues.customerId || "").trim();
    let isMounted = true;

    if (!nextCustomerId) {
      customerRequestIdRef.current += 1;
      setContractOptions([]);
      setContractLoading(false);
      setInvoiceValues((prev) => ({ ...prev, billingCadence: "—" }));
      setSelectedPaymentTerm("");
      return;
    }

    const requestId = customerRequestIdRef.current + 1;
    customerRequestIdRef.current = requestId;
    setContractLoading(true);

    const fetchContractsAndDetails = async () => {
      try {
        const response = await apiService.getCustomerTripDetail(nextCustomerId);

        if (!isMounted || customerRequestIdRef.current !== requestId) {
          return;
        }

        const customerTripDetailData = response?.data || response || {};
        const mappedContracts = mapCustomerContractsToOptions(customerTripDetailData?.contracts);
        setContractOptions(mappedContracts);

        // Fetch customer specific billing cadence if present
        const cadence = customerTripDetailData?.billing_cadence || customerTripDetailData?.cadence || "Monthly";

        setInvoiceValues((prev) => ({
          ...prev,
          contractId: "",
          contractLabel: "",
          billingCadence: cadence,
        }));
        setSelectedPaymentTerm("");

        // Fetch columns configuration for customer to extract IBAN
        try {
          const configRes = await apiService.getInvoiceColumnConfigsPaginated({ page: 1, limit: 100 });
          const configs = configRes?.data?.results || configRes?.results || [];
          const matched = configs.find((c) => String(c.customer_id) === String(nextCustomerId));
          setColumnConfig(matched || null);
        } catch (e) {
          console.error("Failed to load columns config", e);
        }
      } catch (error) {
        if (!isMounted || customerRequestIdRef.current !== requestId) {
          return;
        }
        setContractOptions([]);
        errorNotify(error || "Failed to fetch customer contracts");
      } finally {
        if (isMounted && customerRequestIdRef.current === requestId) {
          setContractLoading(false);
        }
      }
    };

    fetchContractsAndDetails();

    return () => {
      isMounted = false;
    };
  }, [invoiceValues.customerId]);

  // Helper search filter for fetched trips
  const filteredTrips = useMemo(() => {
    const list = Array.isArray(tripOptions) ? tripOptions : [];
    const searchStr = String(tripSearch || "").trim().toLowerCase();
    if (!searchStr) return list;

    return list.filter((trip) =>
      JSON.stringify(trip)
        .toLowerCase()
        .includes(searchStr)
    );
  }, [tripOptions, tripSearch]);

  const selectedTripIdsSet = useMemo(
    () => new Set(invoiceValues.selectedTripIds.map((id) => String(id))),
    [invoiceValues.selectedTripIds]
  );

  const eligibleFilteredTrips = useMemo(() => {
    return filteredTrips.filter((t) => Number(t.weight || 0) > 0);
  }, [filteredTrips]);

  const allVisibleSelected =
    eligibleFilteredTrips.length > 0 &&
    eligibleFilteredTrips.every((trip) => selectedTripIdsSet.has(String(trip.value)));

  const activeColumns = useMemo(() => {
    const cols = Array.isArray(invoiceColumns) ? invoiceColumns : [];
    if (cols.length === 0) {
      return [
        { column_key: "trip", custom_name: "Trip No" },
        { column_key: "vehicle_type", custom_name: "Vehicle Type" },
        { column_key: "route_province", custom_name: "Route" },
        { column_key: "weight", custom_name: "Weight" },
        { column_key: "fuel_l", custom_name: "Fuel L" },
        { column_key: "shortage", custom_name: "Shortage" },
        { column_key: "status", custom_name: "Status" },
      ];
    }
    return cols
      .filter((col) => col.is_visible !== false)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  }, [invoiceColumns]);

  const calculationTableColumns = useMemo(() => {
    const cols = activeColumns.filter(
      (c) => c.column_key !== "tax" && c.column_key !== "tax_amount"
    );

    const hasFreight = cols.some(c => c.column_key === "freight" || c.column_key === "freight_base");
    if (!hasFreight) {
      cols.push({ column_key: "freight", custom_name: "Freight" });
    }

    const hasDeduction = cols.some(c => c.column_key === "deduction" || c.column_key === "shortage_deduction");
    if (!hasDeduction) {
      cols.push({ column_key: "deduction", custom_name: "Deduction" });
    }

    const hasTaxRate = cols.some(c => c.column_key === "tax_percentage" || c.column_key === "tax_rate");
    if (!hasTaxRate) {
      cols.push({ column_key: "tax_percentage", custom_name: "Tax %" });
    }

    cols.push({ column_key: "tax_amount", custom_name: "Tax Amount" });

    const hasNet = cols.some(c => c.column_key === "net" || c.column_key === "net_amount");
    if (!hasNet) {
      cols.push({ column_key: "net", custom_name: "Net" });
    }

    return cols;
  }, [activeColumns]);


  const renderColumnValue = (trip, key) => {
    let tripInfo = trip.raw
    const formattedAmount = (val) => {
      const num = Number(val || 0);
      return `PKR ${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    switch (key) {
      case "trip":
        return trip.tripCode || "—";
      case "vehicle_type":
        return trip.vehicleTypeName || "—";
      case "vehicle_no":
      case "vehicle_number":
        return trip.vehicleNo || "—";
      case "route_province":
      case "province_pair":
      case "province": {
        const legs = trip.legs || [];
        if (legs.length === 0) return "—";
        const firstSrc = legs[0]?.source_stop?.province || legs[0]?.province || "—";
        const lastDest = legs[legs.length - 1]?.destination_stop?.province || "—";
        const label = `${firstSrc} → ${lastDest}`;

        if (legs.length === 1) {
          return label;
        }

        return (
          <span
            onClick={() => {
              setLegsModalTitle(`Route Hops Breakdown — ${trip.tripCode}`);
              setLegsModalData(legs);
              setLegsModalOpen(true);
            }}
            style={{
              color: "#1e3a8a",
              fontWeight: 500,
              cursor: "pointer",
              textDecoration: "underline",
              display: "inline-flex",
              alignItems: "center",
              gap: "4px"
            }}
          >
            {label} ({legs.length} Hops)
          </span>
        );
      }
      case "formula":
        return trip.raw?.trips_invoice_record?.formula_text || "—";
      case "origin":
        return trip.raw?.trip?.start_trip?.location || "—";
      case "destination":
        return trip.raw?.trip?.end_trip?.location || "—";
      case "trip_start_datetime":
        return trip.raw?.trip?.actual_start_at
          ? new Date(trip.raw.trip.actual_start_at).toLocaleString()
          : (trip.raw?.trip?.start_at ? new Date(trip.raw.trip.start_at).toLocaleString() : "—");
      case "start_time":
        return trip.raw?.trip?.start_at ? new Date(trip.raw.trip.start_at).toLocaleTimeString() : "—";
      case "trip_end_datetime":
        return trip.raw?.trip?.actual_end_at
          ? new Date(trip.raw.trip.actual_end_at).toLocaleString()
          : (trip.raw?.trip?.end_at ? new Date(trip.raw.trip.end_at).toLocaleString() : "—");
      case "end_time":
        return trip.raw?.trip?.actual_end_at
          ? new Date(trip.raw.trip.actual_end_at).toLocaleTimeString()
          : (trip.raw?.trip?.expected_end_at ? new Date(trip.raw.trip.expected_end_at).toLocaleTimeString() : "—");
      case "overtime":
        return trip.raw?.trip_data?.overtime || trip.raw?.overtime || "0";
      case "no_of_trips":
        return trip.raw?.no_of_trips || "1";
      case "weight": {
        const totalLoad = trip.raw?.trip?.trip_load_sum?.total_load || trip.weight;
        return totalLoad !== undefined && totalLoad !== null && totalLoad !== "" ? totalLoad : "—";
      }
      case "weight_rate":
        return trip.raw?.trip_data?.weight_rate ? formattedAmount(trip.raw.trip_data.weight_rate) : "—";
      case "location_rate":
        return trip.legs
          ?.map((leg) => leg.location_rates?.location_rate)
          .filter(Boolean)
          .join(", ") || "0";
      case "distance":
      case "total_odometer":
        return trip.raw?.trip_data?.odometer_distance || trip.raw?.trip?.total_odm || trip.raw?.trip?.distance || "0";
      case "start_odm":
      case "start_odometer":
        return trip.raw?.trip?.start_odm || trip.raw?.start_odm || "0";
      case "end_odm":
      case "end_odometer":
        return trip.raw?.trip?.end_odm || trip.raw?.end_odm || "0";
      case "fuel_l":
      case "fuel_litres":
        return trip.fuelQty;
      case "per_trip_fuel":
        return trip.raw?.trip_data?.per_trip_fuel || "0";
      case "fuel_price":
        if (trip.averageFuelPrice) return formattedAmount(trip.averageFuelPrice);
        return trip.raw?.trip_data?.fuel_price ? formattedAmount(trip.raw.trip_data.fuel_price) : "—";
      case "shortage":
      case "shortage_quantity":
        return trip.shortageQty;
      case "fuel_expense":
        return formattedAmount(trip.raw?.total_fuel_expense);
      case "trip_expense":
        return formattedAmount(trip.raw?.total_operational_expenses);
      case "toll_charges":
        return formattedAmount(trip.raw?.trip_data?.fix_charges?.find(c => c.type === "toll")?.rate || 0);
      case "other_expenses":
        return formattedAmount(trip.raw?.trip_data?.fix_charges?.filter(c => c.type !== "toll")?.reduce((sum, c) => sum + Number(c.rate), 0) || 0);
      case "freight":
      case "freight_base":
        return formattedAmount(trip.freight);
      case "deduction":
      case "shortage_deduction":
        return formattedAmount(trip.deduction);
      case "tax_percentage":
      case "tax_rate": {
        const tripTotals = invoiceValues?.invoiceInfoRawData?.trip_totals;
        const invoiceLevelTripTax = Number(
          tripTotals?.tax_percentage ?? invoiceValues?.invoiceInfoRawData?.trip_tax_percentage,
        );
        const resolvedTaxRate = Number.isFinite(invoiceLevelTripTax) && invoiceLevelTripTax > 0
          ? invoiceLevelTripTax
          : Number(trip.taxRate);
        return `${resolvedTaxRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
      }
      case "tax":
      case "tax_amount": {
        const tripTotals = invoiceValues?.invoiceInfoRawData?.trip_totals;
        return formattedAmount(tripTotals?.tax_amount ?? trip.tax);
      }
      case "net":
      case "net_amount": {
        const tripTotals = invoiceValues?.invoiceInfoRawData?.trip_totals;
        return formattedAmount(tripTotals?.net_amount ?? trip.net);
      }
      case "gross_amount":
        return formattedAmount(trip.raw?.invoice_gross_amount);
      case "taxable_amount":
        return formattedAmount(trip.raw?.invoice_taxable_amount);
      case "equipment_id":
        return trip.raw?.trip?.equipment_id || "—";
      case "description":
        return trip.raw?.trip?.description ? <div dangerouslySetInnerHTML={{ __html: trip.raw.trip.description }} /> : "—";
      default:
        return "—";
    }
  };

  const handleToggleTrip = (tripId) => {
    const normalizedId = String(tripId);
    setInvoiceValues((prev) => {
      const nextIds = prev.selectedTripIds.includes(normalizedId)
        ? prev.selectedTripIds.filter((id) => id !== normalizedId)
        : [...prev.selectedTripIds, normalizedId];

      const matchingTrips = tripOptions.filter((t) => nextIds.includes(String(t.value)));

      return {
        ...prev,
        selectedTripIds: nextIds,
        selectedTrips: matchingTrips,
        invoiceInfoCalculation: null,
      };
    });
    setInvoiceErrors((prev) => ({ ...prev, selectedTripIds: "" }));
  };

  const handleToggleSelectAll = () => {
    setInvoiceValues((prev) => {
      const eligibleIds = eligibleFilteredTrips.map((t) => String(t.value));
      const nextIds = allVisibleSelected
        ? prev.selectedTripIds.filter((id) => !eligibleIds.includes(id))
        : Array.from(new Set([...prev.selectedTripIds, ...eligibleIds]));

      const matchingTrips = tripOptions.filter((t) => nextIds.includes(String(t.value)));

      return {
        ...prev,
        selectedTripIds: nextIds,
        selectedTrips: matchingTrips,
        invoiceInfoCalculation: null,
      };
    });
    setInvoiceErrors((prev) => ({ ...prev, selectedTripIds: "" }));
  };

  const setFieldValue = (field, value, clearError = false) => {
    setInvoiceValues((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (clearError) {
      setInvoiceErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleCustomerChange = (opt) => {
    setInvoiceValues((prev) => ({
      ...prev,
      customerId: opt ? opt.value : "",
      customerLabel: opt ? opt.label : "",
      contractId: "",
      contractLabel: "",
      selectedTripIds: [],
      selectedTrips: [],
      invoiceInfoCalculation: null,
    }));
    setSelectedPaymentTerm("");
    setInvoiceErrors((prev) => ({ ...prev, customerId: "", contractId: "" }));
  };

  const handleContractChange = (opt) => {
    const selectedOption = contractSelectOptions.find((o) => String(o.value) === String(opt?.value));
    const paymentTerm = selectedOption?.payment_term || "";
    setSelectedPaymentTerm(paymentTerm);

    setInvoiceValues((prev) => ({
      ...prev,
      contractId: opt ? opt.value : "",
      contractLabel: opt ? opt.label : "",
      selectedTripIds: [],
      selectedTrips: [],
      invoiceInfoCalculation: null,
    }));
    setInvoiceErrors((prev) => ({ ...prev, contractId: "" }));
  };

  const validateStep = (step) => {
    const errors = {};
    if (step === 0) {
      if (isEmptyValue(invoiceValues.customerId)) errors.customerId = "Customer is required";
      if (isEmptyValue(invoiceValues.contractId)) errors.contractId = "Contract is required";
      if (isEmptyValue(invoiceValues.invoiceType)) errors.invoiceType = "Invoice Type is required";
      if (!invoiceValues.startAt) errors.startAt = "Period From is required";
      if (!invoiceValues.endAt) errors.endAt = "Period To is required";
      if (!invoiceValues.invoice_at) errors.invoice_at = "Invoice Date is required";
      if (!invoiceValues.invoice_due_at) errors.invoice_due_at = "Due Date is required";
      if (invoiceValues.includeMonthlyRental && isEmptyValue(invoiceValues.rental_tax_percentage)) {
        errors.rental_tax_percentage = "Rental Tax is required";
      }
    } else if (step === 1) {
      if (invoiceValues.selectedTripIds.length === 0) {
        errors.selectedTripIds = "At least one trip must be selected";
      }
    }
    setInvoiceErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const selectedContractDisplayName = useMemo(() => {
    const selectedContractId = toTrimmedText(invoiceValues.contractId);
    const selectedContractLabel = stripTrailingIdentifier(invoiceValues.contractLabel);
    if (!selectedContractId && selectedContractLabel) return selectedContractLabel;

    const matchedContract = (Array.isArray(contractOptions) ? contractOptions : []).find(
      (option) => String(option?.value ?? "") === selectedContractId
    );

    return stripTrailingIdentifier(matchedContract?.label) ||
      selectedContractLabel ||
      (selectedContractId ? `Contract ${selectedContractId}` : "Contract");
  }, [contractOptions, invoiceValues.contractId, invoiceValues.contractLabel]);

  const fetchEligibleTrips = async () => {
    setTripLoading(true);
    try {
      const rentalTaxPercentage = toPercentageNumber(invoiceValues.rental_tax_percentage);
      const tripTaxPercentage = toPercentageNumber(invoiceValues.trip_tax_percentage);
      const payload = {
        contract_id: toApiIdentifier(invoiceValues.contractId),
        customer_id: toApiIdentifier(invoiceValues.customerId),
        from: formatDateTimeForPayload(invoiceValues.startAt, "start"),
        to: formatDateTimeForPayload(invoiceValues.endAt, "end"),
        start_at: formatDateTimeForPayload(invoiceValues.startAt, "start"),
        end_at: formatDateTimeForPayload(invoiceValues.endAt, "end"),
        invoice_type: invoiceValues.invoiceType,
        include_monthly_rental: Boolean(invoiceValues.includeMonthlyRental),
        rental_tax_percentage: rentalTaxPercentage,
        trip_tax_percentage: tripTaxPercentage,
      };
      const response = await apiService.getEligibleTrips(payload);

      const rawList = Array.isArray(response?.data?.trips)
        ? response.data.trips
        : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : Array.isArray(response?.results)
              ? response.results
              : [];

      const columnsList = response?.data?.invoice_columns || response?.invoice_columns || [];
      setInvoiceColumns(columnsList);
      const tripsList = rawList
        .map((item) => normalizeTripData(item, selectedContractDisplayName))
        .filter(Boolean);

      const uniqueTrips = [];
      const seenIds = new Set();
      tripsList.forEach((t) => {
        if (t && !seenIds.has(t.value)) {
          seenIds.add(t.value);
          uniqueTrips.push(t);
        }
      });

      setTripOptions(uniqueTrips);
      return true;
    } catch (error) {
      errorNotify(error || "Failed to fetch eligible trips");
      return false;
    } finally {
      setTripLoading(false);
    }
  };

  const fetchInvoiceInfoPreview = async () => {
    const requestId = invoiceInfoRequestIdRef.current + 1;
    invoiceInfoRequestIdRef.current = requestId;
    setInvoiceInfoLoading(true);

    try {
      const payload = buildCalculationPreviewPayload(invoiceValues);
      const response = await apiService.getCalculationPreview(payload);

      if (invoiceInfoRequestIdRef.current !== requestId) {
        return null;
      }

      const columnsList = response?.data?.invoice_columns || response?.invoice_columns || [];
      if (columnsList.length > 0) {
        setInvoiceColumns(columnsList);
      }

      const nextCalculation = buildTripInvoiceInfoCalculation(response);
      setInvoiceValues((prev) => ({
        ...prev,
        invoiceInfoCalculation: nextCalculation,
        invoiceInfoRawData: response?.data || response || null,
      }));
      return nextCalculation;
    } catch (error) {
      errorNotify(error || "Failed to fetch calculation preview");
      return null;
    } finally {
      if (invoiceInfoRequestIdRef.current === requestId) {
        setInvoiceInfoLoading(false);
      }
    }
  };

  const handleNext = async () => {
    if (currentFormStep === 0) {
      if (validateStep(0)) {
        const success = await fetchEligibleTrips();
        if (success) {
          setCurrentFormStep(1);
        }
      }
    } else if (currentFormStep === 1) {
      if (validateStep(1)) {
        const nextCalculation = await fetchInvoiceInfoPreview();
        if (nextCalculation) {
          setCurrentFormStep(2);
        }
      }
    } else if (currentFormStep === 2) {
      setCurrentFormStep(3);
    }
  };

  const handlePrevious = () => {
    setCurrentFormStep((prev) => Math.max(prev - 1, 0));
  };

  const handleSubmitForApproval = async () => {
    if (invoiceSaving) return;
    setInvoiceSaving(true);

    try {
      // Build create payload
      const createPayload = buildBulkTripInvoiceCreatePayload(invoiceValues);
      await apiService.createTripInvoice(createPayload);

      // Save to local storage for details page display
      const nextRecord = buildBulkInvoiceRecordFromForm({
        values: invoiceValues,
        createdBy: currentUser?.username || currentUser?.name || "AJCL Admin",
        createdByImage: currentUser?.image || "",
        calculationOverride: invoiceValues.invoiceInfoCalculation,
      });

      const nextRecords = [nextRecord, ...records];
      saveInvoiceRecords(nextRecords);
      setRecords(nextRecords);

      successNotify("Invoice submitted for approval successfully");
      navigate("/invoices");
    } catch (error) {
      errorNotify(error || "Failed to submit invoice");
    } finally {
      setInvoiceSaving(false);
    }
  };

  // Step 3 Table Row details parsing (with standardized mapping)
  const calculationTrips = useMemo(() => {
    const rawTrips = invoiceValues.invoiceInfoRawData?.trips || [];
    return invoiceValues.selectedTrips.map((trip) => {
      const matchedCalculatedTrip = rawTrips.find(
        (t) => String(t?.trip?.id || t?.trip_id) === String(trip.value)
      );
      if (!matchedCalculatedTrip) return null;
      return normalizeTripData(matchedCalculatedTrip, selectedContractDisplayName);
    }).filter(Boolean);
  }, [invoiceValues.selectedTrips, invoiceValues.invoiceInfoRawData, selectedContractDisplayName]);

  const formatPkr = (value) =>
    Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const monthlyRentals = Array.isArray(invoiceValues.invoiceInfoRawData?.monthly_rentals)
    ? invoiceValues.invoiceInfoRawData.monthly_rentals
    : [];
  const monthlyRentalTotals = invoiceValues.invoiceInfoRawData?.monthly_rental_totals || null;
  const overallTotals = invoiceValues.invoiceInfoRawData?.overall_totals || null;
  const showMonthlyRentalSection = monthlyRentals.length > 0;

  // Total invoice summary
  const invoicePreviewSummary = useMemo(() => {
    const tripTotals = invoiceValues.invoiceInfoRawData?.trip_totals;
    const subtotal = invoiceValues.invoiceInfoCalculation?.subtotal ?? 0;
    const tax = tripTotals?.tax_amount ?? invoiceValues.invoiceInfoCalculation?.salesTax ?? 0;
    const discount = invoiceValues.invoiceInfoCalculation?.discount ?? 0;
    const totalAmount = invoiceValues.invoiceInfoCalculation?.totalAmount ?? 0;

    return {
      subtotal,
      tax,
      discount,
      totalAmount,
    };
  }, [invoiceValues.invoiceInfoCalculation, invoiceValues.invoiceInfoRawData]);

  const customerOptions = useMemo(
    () => prependMissingOption(customerDropdownData, {
      value: invoiceValues.customerId,
      label: invoiceValues.customerLabel,
    }),
    [customerDropdownData, invoiceValues.customerId, invoiceValues.customerLabel]
  );

  const contractSelectOptions = useMemo(
    () => prependMissingOption(contractOptions, {
      value: invoiceValues.contractId,
      label: invoiceValues.contractLabel,
    }),
    [contractOptions, invoiceValues.contractId, invoiceValues.contractLabel]
  );

  const activeIbanNumber = columnConfig?.iban_number || "PK00JHCO00000000000123456";
  const showTaxLine = columnConfig?.show_tax_line ?? true;
  const showPaymentGuidelines = columnConfig?.show_payment_guidelines ?? true;

  if (pageLoading) {
    return (
      <div className="create_vehicle_page trip_cost_estimation_create_page invoice_create_page">
        <Breadcrumbs values={["Finance", "Invoices", "Create"]} />
        <div className="page_container">
          <SpinnerLoader />
        </div>
      </div>
    );
  }

  return (
    <div className="create_vehicle_page trip_cost_estimation_create_page invoice_create_page">
      <Breadcrumbs values={["Finance", "Invoices", "Create"]} />

      <div className="page_header access_view">
        <h2>Create Invoice</h2>
        <div className="d-flex align-items-center gap-2">
          <Button
            cancelBtn={() => navigate("/invoices")}
            submitBtn={() => { }}
            type="button"
            shouldRow={false}
            isShowSubmitBtn={false}
            cancelBtnText="Cancel"
          />
        </div>
      </div>

      <div className="page_container">
        <Row className="driver_page_layout trip_cost_estimation_create_layout invoice_create_layout">
          <Col lg={12} className="invoice_create_main_col">
            <div className="trip_information_main_div driver_form_shell invoice_create_shell">
              <h5>Invoice Information</h5>

              <div className="driver_form_content">
                <div className="driver_payments_modal_idbar invoice_create_idbar">
                  <span>Invoice ID:</span>
                  <strong>{invoiceValues.invoiceId}</strong>
                </div>

                <div className="d-flex trip_create_steps_layout">
                  <div className="trip_create_steps_sidebar">
                    <Steps
                      className="transporter_steps trip_create_steps form_steps_vertical"
                      current={currentFormStep}
                      direction="vertical"
                      items={INVOICE_STEPS}
                      onChange={(target) => {
                        if (target <= currentFormStep && currentFormStep < 4) {
                          setCurrentFormStep(target);
                        }
                      }}
                    />
                  </div>

                  <div className="driver_payments_modal_step_content invoice_create_step_content w-100 mb-3">

                    {/* Step 1: Customer & Period */}
                    {currentFormStep === 0 && (
                      <div className="driver_form_section">
                        <Row className="gy-3">
                          <Col md={12}>
                            <Select
                              label={renderFieldLabel("Customer")}
                              placeholder={dropdownLoading ? "Loading Customers..." : "Select Customer"}
                              options={customerOptions}
                              value={pickOption(customerOptions, invoiceValues.customerId)}
                              name="customerId"
                              onChange={handleCustomerChange}
                              error={invoiceErrors.customerId}
                              touched={Boolean(invoiceErrors.customerId)}
                              isSearch
                              isDisabled={dropdownLoading}
                            />
                          </Col>

                          <Col md={6}>
                            <Select
                              label={renderFieldLabel("Contract")}
                              placeholder={contractLoading ? "Loading Contracts..." : "Select Contract"}
                              options={contractSelectOptions}
                              value={pickOption(contractSelectOptions, invoiceValues.contractId)}
                              name="contractId"
                              onChange={handleContractChange}
                              error={invoiceErrors.contractId}
                              touched={Boolean(invoiceErrors.contractId)}
                              isSearch
                              isDisabled={isEmptyValue(invoiceValues.customerId) || contractLoading}
                            />
                          </Col>

                          <Col md={6}>
                            <Select
                              label={renderFieldLabel("Invoice Type")}
                              placeholder="Select Invoice Type"
                              options={[
                                { label: "Monthly", value: INVOICE_TYPES.MONTHLY },
                                { label: "Weekly", value: INVOICE_TYPES.WEEKLY },
                                { label: "AdHoc", value: INVOICE_TYPES.AD_HOC },
                              ]}
                              value={
                                invoiceValues.invoiceType
                                  ? {
                                    label:
                                      invoiceValues.invoiceType === INVOICE_TYPES.MONTHLY
                                        ? "Monthly"
                                        : invoiceValues.invoiceType === INVOICE_TYPES.WEEKLY
                                          ? "Weekly"
                                          : "AdHoc",
                                    value: invoiceValues.invoiceType,
                                  }
                                  : null
                              }
                              name="invoiceType"
                              onChange={(opt) => setFieldValue("invoiceType", opt ? opt.value : "", true)}
                              error={invoiceErrors.invoiceType}
                              touched={Boolean(invoiceErrors.invoiceType)}
                            />
                          </Col>

                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("Period From")}
                              type="datetime-local"
                              name="startAt"
                              value={invoiceValues.startAt}
                              onChange={(e) => setFieldValue("startAt", e.target.value, true)}
                              error={invoiceErrors.startAt}
                              touched={Boolean(invoiceErrors.startAt)}
                            />
                          </Col>

                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("Period To")}
                              type="datetime-local"
                              name="endAt"
                              value={invoiceValues.endAt}
                              onChange={(e) => setFieldValue("endAt", e.target.value, true)}
                              error={invoiceErrors.endAt}
                              touched={Boolean(invoiceErrors.endAt)}
                            />
                          </Col>

                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("Invoice Date")}
                              type="date"
                              name="invoice_at"
                              value={invoiceValues.invoice_at}
                              onChange={(e) => setFieldValue("invoice_at", e.target.value, true)}
                              error={invoiceErrors.invoice_at}
                              touched={Boolean(invoiceErrors.invoice_at)}
                            />
                          </Col>

                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("Due Date")}
                              type="date"
                              name="invoice_due_at"
                              value={invoiceValues.invoice_due_at}
                              onChange={(e) => setFieldValue("invoice_due_at", e.target.value, true)}
                              error={invoiceErrors.invoice_due_at}
                              touched={Boolean(invoiceErrors.invoice_due_at)}
                            />
                          </Col>

                          <Col md={12}>
                            <label
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "10px",
                                fontSize: "14px",
                                fontWeight: 600,
                                color: "#334155",
                                cursor: "pointer",
                                userSelect: "none",
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={Boolean(invoiceValues.includeMonthlyRental)}
                                onChange={(event) => {
                                  const checked = event.target.checked;
                                  setInvoiceValues((prev) => ({
                                    ...prev,
                                    includeMonthlyRental: checked,
                                    rental_tax_percentage: checked ? prev.rental_tax_percentage : "",
                                  }));
                                  setInvoiceErrors((prev) => ({ ...prev, rental_tax_percentage: "" }));
                                }}
                                style={{
                                  width: "16px",
                                  height: "16px",
                                  cursor: "pointer",
                                  accentColor: "#4f18d6",
                                }}
                              />
                              <span>Apply Monthly Rental</span>
                            </label>
                          </Col>

                          {invoiceValues.includeMonthlyRental && (
                            <Col md={6}>
                              <Input
                                label={renderFieldLabel("Rental Tax (%)", true)}
                                placeholder="0.00"
                                type="number"
                                name="rental_tax_percentage"
                                value={invoiceValues.rental_tax_percentage}
                                onChange={(e) =>
                                  setFieldValue(
                                    "rental_tax_percentage",
                                    clampPercentageInput(e.target.value),
                                    true
                                  )
                                }
                                error={invoiceErrors.rental_tax_percentage}
                                touched={Boolean(invoiceErrors.rental_tax_percentage)}
                                minValue={0}
                                maxValue={100}
                                step="0.01"
                                showExtraText
                                extraText="%"
                              />
                            </Col>
                          )}

                          <Col md={invoiceValues.includeMonthlyRental ? 6 : 12}>
                            <Input
                              label={renderFieldLabel("Trip Tax (%)", false)}
                              placeholder="0.00"
                              type="number"
                              name="trip_tax_percentage"
                              value={invoiceValues.trip_tax_percentage}
                              onChange={(e) =>
                                setFieldValue(
                                  "trip_tax_percentage",
                                  clampPercentageInput(e.target.value),
                                  true
                                )
                              }
                              error={invoiceErrors.trip_tax_percentage}
                              touched={Boolean(invoiceErrors.trip_tax_percentage)}
                              minValue={0}
                              maxValue={100}
                              step="0.01"
                              showExtraText
                              extraText="%"
                            />
                          </Col>

                          <Col md={12}>
                            <Input
                              label={renderFieldLabel("Description", false)}
                              placeholder="Write detail if any..."
                              name="notes"
                              value={invoiceValues.notes}
                              onChange={(e) => setFieldValue("notes", e.target.value, true)}
                              error={invoiceErrors.notes}
                              touched={Boolean(invoiceErrors.notes)}
                              quill
                              setFieldValue={(field, val) => setFieldValue("notes", val, true)}
                              className="pricing_template_description_input"
                              required={false}
                            />
                          </Col>

                          <Col md={12} className="mt-4">
                            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
                              <Button
                                cancelBtn={() => { }}
                                submitBtn={handleNext}
                                submitText="Next"
                                type="button"
                                shouldRow={false}
                                cancelBtnDisabled={true}
                                cancelBtnText="Back"
                                isShowCancel={false}
                              />
                            </div>
                          </Col>
                        </Row>
                      </div>
                    )}

                    {/* Step 2: Eligible Trips */}
                    {currentFormStep === 1 && (
                      <div className="driver_form_section">
                        <div style={{ background: "#f8fafc", padding: "16px", borderRadius: "8px", border: "1px solid #e2e8f0", marginBottom: "20px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#475569", fontSize: "14px" }}>
                            <FiInfo style={{ color: "#3b82f6", flexShrink: 0 }} />
                            <span>
                              Closed, un-invoiced trips for <strong>{invoiceValues.customerLabel || "selected customer"}</strong> in period. Validation follows formula tokens (fuel litres only required when used).
                            </span>
                          </div>
                        </div>

                        <div className="bulk_invoice_searchbar mb-3" style={{ position: "relative", border: "1px solid #cbd5e1", borderRadius: "8px", display: "flex", alignItems: "center", padding: "8px 12px", background: "#fff" }}>
                          <FiSearch style={{ color: "#94a3b8", marginRight: "8px" }} />
                          <input
                            type="text"
                            value={tripSearch}
                            onChange={(event) => setTripSearch(event.target.value)}
                            placeholder="Search Trips"
                            style={{ border: "none", outline: "none", width: "100%", fontSize: "14px", color: "#334155" }}
                          />
                        </div>

                        <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden" }}>
                          <Table responsive hover className="mb-0 align-middle">
                            <thead style={{ background: "#f8fafc" }}>
                              <tr>
                                <th style={{ width: "40px", padding: "12px 16px" }}>
                                  <input
                                    type="checkbox"
                                    checked={allVisibleSelected}
                                    onChange={handleToggleSelectAll}
                                    disabled={!eligibleFilteredTrips.length}
                                  />
                                </th>
                                {activeColumns.map((col) => (
                                  <th
                                    key={col.column_key}
                                    style={{
                                      fontSize: "12px",
                                      textTransform: "uppercase",
                                      color: "#64748b",
                                      fontWeight: 600,
                                      padding: "12px 16px",
                                    }}
                                  >
                                    {(col.custom_name || col.default_name || col.column_key || "").replace(/[-_]/g, " ")}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {filteredTrips.length > 0 ? (
                                filteredTrips.map((trip) => {
                                  const weight = trip.weight || trip.cargo_weight || 0;
                                  const isEligible = weight > 0;

                                  return (
                                    <tr key={trip.value}>
                                      <td style={{ padding: "12px 16px" }}>
                                        <input
                                          type="checkbox"
                                          checked={selectedTripIdsSet.has(String(trip.value))}
                                          onChange={() => handleToggleTrip(trip.value)}
                                          disabled={!isEligible}
                                        />
                                      </td>
                                      {activeColumns.map((col) => (
                                        <td
                                          key={col.column_key}
                                          style={{
                                            fontSize: "14px",
                                            color: "#334155",
                                            padding: "12px 16px",
                                            fontWeight: col.column_key === "trip" ? 500 : "normal",
                                          }}
                                        >
                                          {renderColumnValue(trip, col.column_key)}
                                        </td>
                                      ))}
                                    </tr>
                                  );
                                })
                              ) : (
                                <tr>
                                  <td colSpan={activeColumns.length + 1} className="text-center" style={{ padding: "20px", color: "#64748b" }}>
                                    {tripLoading ? "Loading trips..." : "No eligible trips found in selected period."}
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </Table>
                        </div>

                        {invoiceErrors.selectedTripIds ? (
                          <div style={{ color: "#dc2626", fontSize: "12px", marginTop: "6px" }}>
                            {invoiceErrors.selectedTripIds}
                          </div>
                        ) : null}

                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "24px" }}>
                          <Button
                            cancelBtn={handlePrevious}
                            cancelBtnText="Previous"
                            submitBtn={handleNext}
                            submitText={invoiceInfoLoading ? "Loading..." : "Next"}
                            type="button"
                            shouldRow={false}
                            loading={invoiceInfoLoading}
                          />
                        </div>
                      </div>
                    )}

                    {/* Step 3: Calculation Preview */}
                    {currentFormStep === 2 && (
                      <div className="driver_form_section">
                        <div style={{ background: "#f8fafc", padding: "16px", borderRadius: "8px", border: "1px solid #e2e8f0", marginBottom: "20px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#475569", fontSize: "14px" }}>
                            <FiInfo style={{ color: "#3b82f6", flexShrink: 0 }} />
                            <span>
                              Each trip uses the formula for its <strong>vehicle type</strong>; tax matched by source/destination province.
                            </span>
                          </div>
                        </div>

                        <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden", marginBottom: "24px" }}>
                          <Table responsive hover className="mb-0 align-middle">
                            <thead style={{ background: "#f8fafc" }}>
                              <tr>
                                {calculationTableColumns.map((col) => (
                                  <th
                                    key={col.column_key}
                                    style={{
                                      fontSize: "12px",
                                      textTransform: "uppercase",
                                      color: "#64748b",
                                      fontWeight: 600,
                                      padding: "12px 16px",
                                    }}
                                  >
                                    {(col.custom_name || col.default_name || col.column_key || "").replace(/[-_]/g, " ")}
                                  </th>
                                ))}
                                <th style={{ fontSize: "12px", textTransform: "uppercase", color: "#64748b", fontWeight: 600, padding: "12px 16px" }}>Formula</th>
                                <th style={{ width: "100px", padding: "12px 16px" }}></th>
                              </tr>
                            </thead>
                            <tbody>
                              {calculationTrips.length > 0 ? (
                                calculationTrips.map((calc, i) => (
                                  <tr key={i}>
                                    {calculationTableColumns.map((col) => (
                                      <td
                                        key={col.column_key}
                                        style={{
                                          fontSize: "14px",
                                          color: col.column_key === "deduction" || col.column_key === "shortage_deduction" ? "#dc2626" : "#334155",
                                          padding: "12px 16px",
                                          fontWeight: col.column_key === "trip" || col.column_key === "net" || col.column_key === "net_amount" ? 600 : "normal",
                                        }}
                                      >
                                        {renderColumnValue(calc, col.column_key)}
                                      </td>
                                    ))}
                                    <td style={{ fontSize: "14px", color: "#334155", padding: "12px 16px" }}>
                                      {calc.formula || "—"}
                                    </td>
                                    <td style={{ padding: "12px 16px" }}>
                                      <button
                                        type="button"
                                        className="btn btn-sm invoice_working_btn"
                                        onClick={() => {
                                          const tripTotals = invoiceValues?.invoiceInfoRawData?.trip_totals;
                                          const invoiceLevelTripTax = Number(
                                            tripTotals?.tax_percentage ?? invoiceValues?.invoiceInfoRawData?.trip_tax_percentage,
                                          );
                                          const resolvedTaxRate = Number.isFinite(invoiceLevelTripTax) && invoiceLevelTripTax > 0
                                            ? invoiceLevelTripTax
                                            : Number(calc.taxRate);
                                          setWorkingDrawerTitle(`Calculation Working — ${calc.tripCode}`);
                                          setWorkingDrawerData({
                                            ...calc.workingDetails,
                                            taxPercentage: resolvedTaxRate,
                                            taxAmount: tripTotals?.tax_amount ?? calc.workingDetails.taxAmount,
                                            finalAmount: tripTotals?.net_amount ?? calc.workingDetails.finalAmount,
                                          });
                                          setWorkingDrawerOpen(true);
                                        }}
                                      >
                                        Working
                                      </button>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={calculationTableColumns.length + 2} className="text-center" style={{ padding: "20px", color: "#64748b" }}>
                                    No calculations available.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </Table>
                        </div>

                        {showMonthlyRentalSection && (
                          <div className="mb-4">
                            <h6 style={{ fontWeight: 600, color: "#475569", marginBottom: "12px", fontSize: "14px" }}>Monthly Rental Breakdown</h6>
                            <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden", background: "#fff" }}>
                              <Table responsive className="mb-0">
                                <thead style={{ background: "#f8fafc" }}>
                                  <tr>
                                    <th style={{ fontSize: "11px", color: "#64748b", fontWeight: 600, textTransform: "uppercase" }}>Vehicle Type</th>
                                    <th style={{ fontSize: "11px", color: "#64748b", fontWeight: 600, textTransform: "uppercase" }}>No. of Vehicles</th>
                                    <th style={{ fontSize: "11px", color: "#64748b", fontWeight: 600, textTransform: "uppercase" }}>Monthly Rental</th>
                                    <th style={{ fontSize: "11px", color: "#64748b", fontWeight: 600, textTransform: "uppercase" }}>Gross Amount</th>
                                    <th style={{ fontSize: "11px", color: "#64748b", fontWeight: 600, textTransform: "uppercase" }}>Tax %</th>
                                    <th style={{ fontSize: "11px", color: "#64748b", fontWeight: 600, textTransform: "uppercase" }}>Tax Amount</th>
                                    <th style={{ fontSize: "11px", color: "#64748b", fontWeight: 600, textTransform: "uppercase" }}>Net Amount</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {monthlyRentals.map((rental, idx) => (
                                    <tr key={rental?.contract_template_vehicle_type_id || idx} style={{ fontSize: "13px" }}>
                                      <td>{rental?.vehicle_type?.title || "—"}</td>
                                      <td>{rental?.no_of_vehicle ?? "—"}</td>
                                      <td>PKR {formatPkr(rental?.monthly_rental)}</td>
                                      <td>PKR {formatPkr(rental?.gross_amount)}</td>
                                      <td>{rental?.tax_percentage ?? 0}%</td>
                                      <td>PKR {formatPkr(rental?.tax_amount)}</td>
                                      <td style={{ fontWeight: 600 }}>PKR {formatPkr(rental?.net_amount)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                                {monthlyRentalTotals && (
                                  <tfoot>
                                    <tr style={{ fontSize: "13px", fontWeight: 700, background: "#f5f3ff" }}>
                                      <td colSpan={3}>Rental Totals</td>
                                      <td>PKR {formatPkr(monthlyRentalTotals.gross_amount)}</td>
                                      <td></td>
                                      <td>PKR {formatPkr(monthlyRentalTotals.tax_amount)}</td>
                                      <td>PKR {formatPkr(monthlyRentalTotals.net_amount)}</td>
                                    </tr>
                                  </tfoot>
                                )}
                              </Table>
                            </div>

                            {overallTotals && (
                              <div
                                style={{
                                  display: "flex",
                                  gap: "24px",
                                  marginTop: "12px",
                                  padding: "12px 16px",
                                  background: "#f5f3ff",
                                  border: "1px solid #ede9fe",
                                  borderRadius: "8px",
                                  fontSize: "13px",
                                }}
                              >
                                <div>
                                  <span style={{ color: "#64748b", display: "block", fontSize: "11px", textTransform: "uppercase" }}>Overall Gross</span>
                                  <strong style={{ color: "#1e293b" }}>PKR {formatPkr(overallTotals.gross_amount)}</strong>
                                </div>
                                <div>
                                  <span style={{ color: "#64748b", display: "block", fontSize: "11px", textTransform: "uppercase" }}>Overall Tax</span>
                                  <strong style={{ color: "#1e293b" }}>PKR {formatPkr(overallTotals.tax_amount)}</strong>
                                </div>
                                <div>
                                  <span style={{ color: "#4f19e2", display: "block", fontSize: "11px", textTransform: "uppercase" }}>Net Amount</span>
                                  <strong style={{ color: "#4f19e2", fontSize: "15px" }}>PKR {formatPkr(overallTotals.net_amount)}</strong>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "24px" }}>
                          <Button
                            cancelBtn={handlePrevious}
                            cancelBtnText="Previous"
                            submitBtn={handleNext}
                            submitText="Next"
                            type="button"
                            shouldRow={false}
                          />
                        </div>
                      </div>
                    )}

                    {/* Step 4: Invoice Preview */}
                    {currentFormStep === 3 && (
                      <div className="driver_form_section">
                        <Card className="border-0 shadow-sm mb-4" style={{ background: "#f8fafc", border: "1px solid #cbd5e1" }}>
                          <Card.Body style={{ padding: "32px" }}>

                            {/* Invoice Header Details */}
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "24px" }}>
                              <div>
                                <h4 style={{ margin: 0, fontWeight: 700, color: "#1e3a8a", fontSize: "20px" }}>{invoiceValues.customerLabel || "Colgate"}</h4>
                                <p style={{ margin: "4px 0", color: "#64748b", fontSize: "13px" }}>
                                  Address: Plot 12, Korangi Industrial Area, Karachi &bull; NTN: 1234567-8 &bull; SNTN: S-1234567
                                </p>
                                <p style={{ margin: "2px 0", color: "#64748b", fontSize: "13px" }}>
                                  Contract: <strong>{calculationTrips[0]?.workingDetails?.contractVersion || selectedContractDisplayName}</strong>
                                </p>
                                <p style={{ margin: "2px 0", color: "#64748b", fontSize: "12px", fontStyle: "italic" }}>
                                  One invoice &mdash; multi-province trips stay together. Columns follow the active Invoice Column Profile.
                                </p>
                              </div>
                              <div style={{ textAlign: "right" }}>
                                <span style={{ fontSize: "12px", color: "#64748b", textTransform: "uppercase" }}>Billing Period</span>
                                <h5 style={{ margin: 0, fontWeight: 600, color: "#334155" }}>
                                  {invoiceValues.startAt} &rarr; {invoiceValues.endAt}
                                </h5>
                              </div>
                            </div>

                            {/* Table of Trips details */}
                            <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden", background: "#fff", marginBottom: "24px" }}>
                              <Table responsive className="mb-0">
                                <thead style={{ background: "#f8fafc" }}>
                                  <tr>
                                    {calculationTableColumns.map((col) => (
                                      <th
                                        key={col.column_key}
                                        style={{ fontSize: "11px", color: "#64748b", fontWeight: 600, textTransform: "uppercase" }}
                                      >
                                        {(col.custom_name || col.default_name || col.column_key || "").replace(/[-_]/g, " ")}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {calculationTrips.map((calc, idx) => (
                                    <tr key={idx} style={{ fontSize: "13px" }}>
                                      {calculationTableColumns.map((col) => (
                                        <td
                                          key={col.column_key}
                                          style={{
                                            fontWeight: col.column_key === "trip" || col.column_key === "net" || col.column_key === "net_amount" ? 600 : "normal",
                                            color: col.column_key === "trip" ? "#1e3a8a" : col.column_key === "deduction" || col.column_key === "shortage_deduction" ? "#dc2626" : "inherit"
                                          }}
                                        >
                                          {renderColumnValue(calc, col.column_key)}
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>

                            {showMonthlyRentalSection && (
                              <div className="mb-4">
                                <h6 style={{ fontWeight: 600, color: "#475569", marginBottom: "12px", fontSize: "14px" }}>Monthly Rental Breakdown</h6>
                                <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden", background: "#fff" }}>
                                  <Table responsive className="mb-0">
                                    <thead style={{ background: "#f8fafc" }}>
                                      <tr>
                                        <th style={{ fontSize: "11px", color: "#64748b", fontWeight: 600, textTransform: "uppercase" }}>Vehicle Type</th>
                                        <th style={{ fontSize: "11px", color: "#64748b", fontWeight: 600, textTransform: "uppercase" }}>No. of Vehicles</th>
                                        <th style={{ fontSize: "11px", color: "#64748b", fontWeight: 600, textTransform: "uppercase" }}>Monthly Rental</th>
                                        <th style={{ fontSize: "11px", color: "#64748b", fontWeight: 600, textTransform: "uppercase" }}>Gross Amount</th>
                                        <th style={{ fontSize: "11px", color: "#64748b", fontWeight: 600, textTransform: "uppercase" }}>Tax %</th>
                                        <th style={{ fontSize: "11px", color: "#64748b", fontWeight: 600, textTransform: "uppercase" }}>Tax Amount</th>
                                        <th style={{ fontSize: "11px", color: "#64748b", fontWeight: 600, textTransform: "uppercase" }}>Net Amount</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {monthlyRentals.map((rental, idx) => (
                                        <tr key={rental?.contract_template_vehicle_type_id || idx} style={{ fontSize: "13px" }}>
                                          <td>{rental?.vehicle_type?.title || "—"}</td>
                                          <td>{rental?.no_of_vehicle ?? "—"}</td>
                                          <td>PKR {formatPkr(rental?.monthly_rental)}</td>
                                          <td>PKR {formatPkr(rental?.gross_amount)}</td>
                                          <td>{rental?.tax_percentage ?? 0}%</td>
                                          <td>PKR {formatPkr(rental?.tax_amount)}</td>
                                          <td style={{ fontWeight: 600 }}>PKR {formatPkr(rental?.net_amount)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                    {monthlyRentalTotals && (
                                      <tfoot>
                                        <tr style={{ fontSize: "13px", fontWeight: 700, background: "#f5f3ff" }}>
                                          <td colSpan={3}>Rental Totals</td>
                                          <td>PKR {formatPkr(monthlyRentalTotals.gross_amount)}</td>
                                          <td></td>
                                          <td>PKR {formatPkr(monthlyRentalTotals.tax_amount)}</td>
                                          <td>PKR {formatPkr(monthlyRentalTotals.net_amount)}</td>
                                        </tr>
                                      </tfoot>
                                    )}
                                  </Table>
                                </div>
                              </div>
                            )}

                            {/* Invoice Summary Calculations */}
                            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                              <div style={{ width: "320px", fontSize: "14px" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", color: "#475569" }}>
                                  <span>Total Freight (Trips)</span>
                                  <strong>PKR {formatAmount(invoicePreviewSummary.subtotal)}</strong>
                                </div>
                                <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", color: "#dc2626" }}>
                                  <span>Total Deduction</span>
                                  <strong>- PKR {formatAmount(invoicePreviewSummary.discount)}</strong>
                                </div>
                                {showTaxLine && (
                                  <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", color: "#475569" }}>
                                    <span>Total Tax (Trips)</span>
                                    <strong>PKR {formatAmount(invoicePreviewSummary.tax)}</strong>
                                  </div>
                                )}
                                {showTaxLine && showMonthlyRentalSection && monthlyRentalTotals && (
                                  <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", color: "#475569" }}>
                                    <span>Rental Tax</span>
                                    <strong>PKR {formatPkr(monthlyRentalTotals.tax_amount)}</strong>
                                  </div>
                                )}
                                <hr style={{ margin: "8px 0", borderColor: "#cbd5e1" }} />
                                <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", color: "#1e3a8a", fontSize: "16px" }}>
                                  <span style={{ fontWeight: 600 }}>Net Amount</span>
                                  <strong style={{ fontWeight: 700 }}>
                                    PKR {overallTotals ? formatPkr(overallTotals.net_amount) : formatAmount(invoicePreviewSummary.totalAmount)}
                                  </strong>
                                </div>
                              </div>
                            </div>

                            {/* Payment guidelines footer */}
                            {showPaymentGuidelines && (
                              <div style={{ background: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: "8px", padding: "16px", marginTop: "24px" }}>
                                <p style={{ margin: 0, color: "#065f46", fontSize: "13px", lineHeight: "1.6" }}>
                                  Payment should be made in favor of <strong>J HOLDINGS (PRIVATE) LIMITED</strong> through crossed cheque, banker's cheque or IBFT to Company's IBAN: <strong>{activeIbanNumber}</strong>
                                </p>
                              </div>
                            )}

                          </Card.Body>
                        </Card>


                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <Button
                            cancelBtn={handlePrevious}
                            cancelBtnText="Previous"
                            submitBtn={handleSubmitForApproval}
                            submitText={invoiceSaving ? "Submitting..." : "Create Invoice"}
                            type="button"
                            shouldRow={false}
                            loading={invoiceSaving}
                          />
                        </div>
                      </div>
                    )}


                  </div>
                </div>
              </div>
            </div>
          </Col>


        </Row>
      </div>

      {/* Calculation working drawer */}
      <CalculationWorkingDrawer
        show={workingDrawerOpen}
        onClose={() => setWorkingDrawerOpen(false)}
        title={workingDrawerTitle}
        data={workingDrawerData}
      />

      {/* Route Hops Breakdown Modal */}
      <Modal
        show={legsModalOpen}
        onHide={() => setLegsModalOpen(false)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton style={{ borderBottom: "1px solid #e2e8f0" }}>
          <Modal.Title style={{ fontSize: "16px", fontWeight: 600, color: "#1e293b" }}>
            {legsModalTitle}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "20px" }}>
          <p style={{ fontSize: "13px", color: "#64748b", marginBottom: "16px" }}>
            Below is the sequence of route stops and province pairs recorded for this trip:
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {legsModalData.map((leg, index) => {
              const s = leg?.source_stop?.province || leg?.province || "—";
              const d = leg?.destination_stop?.province || "—";
              return (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "10px 14px",
                    background: "#f8fafc",
                    borderRadius: "6px",
                    border: "1px solid #e2e8f0",
                    fontSize: "13px",
                    color: "#334155",
                    fontWeight: 500
                  }}
                >
                  {s} &rarr; {d}
                </div>
              );
            })}
          </div>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: "1px solid #e2e8f0", padding: "12px 20px" }}>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => setLegsModalOpen(false)}
            style={{ fontSize: "12px" }}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CreateInvoice;
